import { useEffect } from 'react';
import {
  Box,
  Card,
  Title,
  Text,
  Tag,
  TagProperties,
} from '@nimbus-ds/components';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useOrderSubscriptionsByOrder } from 'domains/Subscriptions/hooks/useOrderSubscription';

interface CardSubscriptionProps {
  orderId: string;
}

function CardSubscription({ orderId }: Readonly<CardSubscriptionProps>) {
  const { items, hasSubscriptions, fetchSubscriptions } =
    useOrderSubscriptionsByOrder(orderId);
  const t = useTranslationOrders();
  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  if (!hasSubscriptions) {
    return null;
  }

  const sub = items?.[0];
  if (!sub?.subscriptionOption) {
    return null;
  }

  const getStatusTagAppearance = (
    status: string,
  ): TagProperties['appearance'] => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'cancelled':
        return 'neutral';
      default:
        return 'primary';
    }
  };

  return (
    <Card>
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Title as="h4">{t('cardSubscription.title')}</Title>
            <Tag
              id={`subscription-status-${sub.id}`}
              appearance={getStatusTagAppearance(sub.status)}
            >
              {t(`cardSubscription.status.${sub.status.toLowerCase()}`)}
            </Tag>
          </Box>
          <Box display="flex" flexDirection="column" gap="2">
            <Box display="flex" justifyContent="space-between">
              <Text
                fontWeight="regular"
                fontSize="base"
                color="neutral-textHigh"
              >
                {t('cardSubscription.idSubscription')}
              </Text>
              <Text
                fontWeight="regular"
                fontSize="base"
                color="neutral-textHigh"
              >
                {sub.id}
              </Text>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Text
                fontWeight="regular"
                fontSize="base"
                color="neutral-textHigh"
              >
                {t('cardSubscription.frequency')}
              </Text>
              <Text
                fontWeight="regular"
                fontSize="base"
                color="neutral-textHigh"
              >
                {sub.subscriptionOption.frequencyParam}{' '}
                {t(
                  `cardSubscription.frequencyType.${sub.subscriptionOption.frequencyType}`,
                )}
              </Text>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Text
                fontWeight="regular"
                fontSize="base"
                color="neutral-textHigh"
              >
                {t('cardSubscription.discount')}
              </Text>
              <Text
                fontWeight="regular"
                fontSize="base"
                color="neutral-textHigh"
              >
                {sub.subscriptionOption.discountPercentage}%
              </Text>
            </Box>
          </Box>
        </Box>
      </Card.Body>
    </Card>
  );
}

export default CardSubscription;
